import type { JSX, SVGProps } from "react";

type IconName =
  | "dashboard"
  | "team"
  | "doc"
  | "user"
  | "settings"
  | "search"
  | "chevron-down"
  | "chevron-right"
  | "plus"
  | "logout"
  | "check"
  | "x"
  | "bell"
  | "external"
  | "mail"
  | "alert"
  | "history"
  | "shield"
  | "filter"
  | "bot"
  | "globe"
  | "plug"
  | "menu"
  | "sun"
  | "moon"
  | "code"
  | "trend";

interface IconProps extends Omit<SVGProps<SVGSVGElement>, "children"> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 16, ...rest }: IconProps): JSX.Element {
  const props: SVGProps<SVGSVGElement> = {
    width: size,
    height: size,
    viewBox: "0 0 16 16",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    ...rest,
    className: ["ico", rest.className].filter(Boolean).join(" "),
  };
  switch (name) {
    case "dashboard":
      return (
        <svg {...props}>
          <rect x="2" y="2" width="5" height="5" rx="1" />
          <rect x="9" y="2" width="5" height="5" rx="1" />
          <rect x="2" y="9" width="5" height="5" rx="1" />
          <rect x="9" y="9" width="5" height="5" rx="1" />
        </svg>
      );
    case "team":
      return (
        <svg {...props}>
          <circle cx="6" cy="5" r="2" />
          <circle cx="11" cy="6" r="1.5" />
          <path d="M2 13c0-2 1.5-3.5 4-3.5s4 1.5 4 3.5M9 13c0-1.5 1-2.5 2.5-2.5S14 11.5 14 13" />
        </svg>
      );
    case "doc":
      return (
        <svg {...props}>
          <path d="M3 2h7l3 3v9H3V2zM10 2v3h3M5 8h6M5 11h4" />
        </svg>
      );
    case "user":
      return (
        <svg {...props}>
          <circle cx="8" cy="6" r="3" />
          <path d="M2 14c0-2.5 2.5-4.5 6-4.5S14 11.5 14 14" />
        </svg>
      );
    case "settings":
      return (
        <svg {...props}>
          <circle cx="8" cy="8" r="2" />
          <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.5 1.5M11.5 11.5L13 13M3 13l1.5-1.5M11.5 4.5L13 3" />
        </svg>
      );
    case "search":
      return (
        <svg {...props}>
          <circle cx="7" cy="7" r="4.5" />
          <path d="M10.5 10.5L14 14" />
        </svg>
      );
    case "chevron-down":
      return (
        <svg {...props}>
          <path d="M4 6l4 4 4-4" />
        </svg>
      );
    case "chevron-right":
      return (
        <svg {...props}>
          <path d="M6 4l4 4-4 4" />
        </svg>
      );
    case "plus":
      return (
        <svg {...props}>
          <path d="M8 3v10M3 8h10" />
        </svg>
      );
    case "logout":
      return (
        <svg {...props}>
          <path d="M9 3H3v10h6M11 5l3 3-3 3M14 8H6" />
        </svg>
      );
    case "check":
      return (
        <svg {...props}>
          <path d="M3 8l3 3 7-7" />
        </svg>
      );
    case "x":
      return (
        <svg {...props}>
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      );
    case "bell":
      return (
        <svg {...props}>
          <path d="M4 12V8a4 4 0 018 0v4l1 1H3l1-1zM6 13.5a2 2 0 004 0" />
        </svg>
      );
    case "external":
      return (
        <svg {...props}>
          <path d="M6 3H3v10h10V10M9 3h4v4M13 3l-6 6" />
        </svg>
      );
    case "mail":
      return (
        <svg {...props}>
          <rect x="2" y="3" width="12" height="10" rx="1" />
          <path d="M2 4l6 5 6-5" />
        </svg>
      );
    case "alert":
      return (
        <svg {...props}>
          <path d="M8 2L1 14h14L8 2zM8 6v4M8 12v.5" />
        </svg>
      );
    case "history":
      return (
        <svg {...props}>
          <circle cx="8" cy="8" r="5.5" />
          <path d="M8 4v4l3 1.5" />
        </svg>
      );
    case "shield":
      return (
        <svg {...props}>
          <path d="M8 2l5 2v4c0 3-5 6-5 6s-5-3-5-6V4l5-2z" />
        </svg>
      );
    case "filter":
      return (
        <svg {...props}>
          <path d="M2 3h12l-4.5 6v4l-3 1.5V9L2 3z" />
        </svg>
      );
    case "bot":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="10" height="8" rx="2" />
          <path d="M8 3v2M5 8.5h.01M11 8.5h.01M6.5 11h3" />
        </svg>
      );
    case "globe":
      return (
        <svg {...props}>
          <circle cx="8" cy="8" r="5.5" />
          <path d="M2.5 8h11M8 2.5c2 2 2 9 0 11M8 2.5c-2 2-2 9 0 11" />
        </svg>
      );
    case "plug":
      return (
        <svg {...props}>
          <path d="M6 2v3M10 2v3M5 5h6v3a3 3 0 01-6 0V5zM8 11v3" />
        </svg>
      );
    case "menu":
      return (
        <svg {...props}>
          <path d="M2 4h12M2 8h12M2 12h12" />
        </svg>
      );
    case "sun":
      return (
        <svg {...props}>
          <circle cx="8" cy="8" r="3" />
          <path d="M8 1v2M8 13v2M1 8h2M13 8h2M2.5 2.5l1.4 1.4M12.1 12.1l1.4 1.4M2.5 13.5l1.4-1.4M12.1 3.9l1.4-1.4" />
        </svg>
      );
    case "moon":
      return (
        <svg {...props}>
          <path d="M13 9.5A5.5 5.5 0 116.5 3a4.5 4.5 0 006.5 6.5z" />
        </svg>
      );
    case "code":
      return (
        <svg {...props}>
          <path d="M5 4l-3 4 3 4M11 4l3 4-3 4M9 3l-2 10" />
        </svg>
      );
    case "trend":
      return (
        <svg {...props}>
          <path d="M2 12l4-4 3 3 5-6M10 5h3v3" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <circle cx="8" cy="8" r="2" />
        </svg>
      );
  }
}
